import { Monitor, Moon, Sun } from 'lucide-react'
import { DropdownMenu, IconButton, SegmentedControl } from '../common'
import { useTheme } from '../../hooks/useTheme'
import { THEMES } from '../../contexts/ThemeContext'
import styles from './ThemeToggle.module.css'

export function ThemeToggle({
  variant = 'icon',
  align = 'end',
  className = '',
}) {
  const { theme, resolvedTheme, setTheme } = useTheme()

  const currentIcon = theme === THEMES.SYSTEM
    ? <Monitor size={17} aria-hidden="true" />
    : resolvedTheme === THEMES.DARK
      ? <Moon size={17} aria-hidden="true" />
      : <Sun size={17} aria-hidden="true" />

  const themeLabel = theme === THEMES.SYSTEM
    ? `Theme: System (${resolvedTheme})`
    : `Theme: ${theme === THEMES.DARK ? 'Dark' : 'Light'}`

  if (variant === 'segmented') {
    return (
      <SegmentedControl
        label="Theme selection"
        value={theme}
        onValueChange={setTheme}
        className={className}
        items={[
          { key: THEMES.SYSTEM, label: 'System' },
          { key: THEMES.LIGHT, label: 'Light' },
          { key: THEMES.DARK, label: 'Dark' },
        ]}
      />
    )
  }

  if (variant === 'inline') {
    return (
      <div className={`${styles.inlineContainer} ${className}`.trim()}>
        <span className={styles.inlineLabel}>Appearance</span>
        <SegmentedControl
          label="Appearance theme"
          value={theme}
          onValueChange={setTheme}
          items={[
            { key: THEMES.SYSTEM, label: 'System' },
            { key: THEMES.LIGHT, label: 'Light' },
            { key: THEMES.DARK, label: 'Dark' },
          ]}
        />
      </div>
    )
  }

  const menuItems = [
    {
      key: THEMES.SYSTEM,
      label: 'System',
      description: `Follow OS appearance (currently ${resolvedTheme})`,
      icon: <Monitor size={16} aria-hidden="true" />,
      selected: theme === THEMES.SYSTEM,
      onSelect: () => setTheme(THEMES.SYSTEM),
    },
    {
      key: THEMES.LIGHT,
      label: 'Light',
      description: 'Clear daylight palette',
      icon: <Sun size={16} aria-hidden="true" />,
      selected: theme === THEMES.LIGHT,
      onSelect: () => setTheme(THEMES.LIGHT),
    },
    {
      key: THEMES.DARK,
      label: 'Dark',
      description: 'Late-night high-contrast palette',
      icon: <Moon size={16} aria-hidden="true" />,
      selected: theme === THEMES.DARK,
      onSelect: () => setTheme(THEMES.DARK),
    },
  ]

  return (
    <div className={`${styles.themeToggle} ${className}`.trim()}>
      <DropdownMenu
        align={align}
        variant="rich"
        selectionMode="single"
        triggerLabel={themeLabel}
        trigger={(
          <IconButton
            variant="ghost"
            label={themeLabel}
            className={styles.themeTrigger}
            aria-label={themeLabel}
          >
            {currentIcon}
          </IconButton>
        )}
        items={menuItems}
      />
    </div>
  )
}
